import { create } from 'zustand'

import {
  clearDownloads,
  type DownloadMeta,
  deleteDownload,
  fetchAndStore,
  getDownloadedIds,
  requestPersistence,
} from '~/lib/offline/downloads'

export type DownloadStatus = 'queued' | 'downloading' | 'error'

const CONCURRENCY = 3

interface OfflineState {
  hydrated: boolean
  downloadedIds: Set<number>
  progress: Record<number, DownloadStatus>
  hydrate: () => Promise<void>
  enqueue: (metas: DownloadMeta[]) => void
  remove: (id: number) => Promise<void>
  removeMany: (ids: number[]) => Promise<void>
  clearAll: () => Promise<void>
}

// Download queue lives outside React state; the store only mirrors status.
let queue: DownloadMeta[] = []
let active = 0

export const useOfflineStore = create<OfflineState>((set, get) => {
  const pump = () => {
    while (active < CONCURRENCY && queue.length > 0) {
      const meta = queue.shift()!
      active++
      set((s) => ({ progress: { ...s.progress, [meta.id]: 'downloading' } }))
      fetchAndStore(meta)
        .then(() => {
          set((s) => {
            const downloadedIds = new Set(s.downloadedIds).add(meta.id)
            const { [meta.id]: _done, ...progress } = s.progress
            return { downloadedIds, progress }
          })
        })
        .catch((err) => {
          console.error('[offline] download failed', meta.id, err)
          set((s) => ({ progress: { ...s.progress, [meta.id]: 'error' } }))
        })
        .finally(() => {
          active--
          pump()
        })
    }
  }

  return {
    hydrated: false,
    downloadedIds: new Set(),
    progress: {},

    hydrate: async () => {
      const ids = await getDownloadedIds()
      set({ downloadedIds: new Set(ids), hydrated: true })
    },

    enqueue: (metas) => {
      const { downloadedIds, progress } = get()
      const toAdd = metas.filter(
        (m) => !downloadedIds.has(m.id) && !progress[m.id] && !queue.some((q) => q.id === m.id)
      )
      if (toAdd.length === 0) return
      void requestPersistence()
      set((s) => {
        const next = { ...s.progress }
        for (const m of toAdd) next[m.id] = 'queued'
        return { progress: next }
      })
      queue.push(...toAdd)
      pump()
    },

    remove: async (id) => {
      queue = queue.filter((q) => q.id !== id)
      await deleteDownload(id)
      set((s) => {
        const downloadedIds = new Set(s.downloadedIds)
        downloadedIds.delete(id)
        const { [id]: _gone, ...progress } = s.progress
        return { downloadedIds, progress }
      })
    },

    removeMany: async (ids) => {
      const idSet = new Set(ids)
      queue = queue.filter((q) => !idSet.has(q.id))
      await Promise.all(ids.map((id) => deleteDownload(id)))
      set((s) => {
        const downloadedIds = new Set(s.downloadedIds)
        const progress = { ...s.progress }
        for (const id of ids) {
          downloadedIds.delete(id)
          delete progress[id]
        }
        return { downloadedIds, progress }
      })
    },

    clearAll: async () => {
      queue = []
      await clearDownloads()
      set({ downloadedIds: new Set(), progress: {} })
    },
  }
})
